/**
 * `QuotaBadgeSlotPied` — WO-V5-D. Consommateur du slot `slotPied` d'`AppSidebar`
 * (livré par la carte A, WO-V5-A) avec le composant `QuotaBadge` (livré par la carte B,
 * `waves/V5/B/integration-note.md`). Affiche la ressource la plus proche de sa limite
 * parmi les quotas d'espace ET les quotas de modèle IA, selon l'algorithme documenté par B.
 *
 * Visible tout le temps (flag `navContexte` ON ou OFF) : le quota n'est PAS derrière le
 * feature flag de navigation contextuelle — seule la barre de contexte l'est.
 */
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import { QuotaBadge, type QuotaBadgeRessource } from '@/components/quota/QuotaBadge'
import { useT } from '@/i18n/language'

export function QuotaBadgeSlotPied({ workspaceId }: { workspaceId: string }): JSX.Element | null {
  const { t } = useT()
  const quota = useQuery({
    queryKey: ['mon-quota', workspaceId],
    queryFn: () => api.monQuota(workspaceId),
  })

  if (quota.isPending || quota.isError || !quota.data) return null

  const { resources, ai_models: modelesIa } = quota.data
  const pire: QuotaBadgeRessource | undefined = [
    ...resources.map((r) => ({
      label: t(`quota.resource.${r.resource_type}`, r.resource_type),
      current_usage: r.current_usage,
      limit_value: r.limit_value,
      percent: r.percent,
      at_warning: r.at_warning,
      at_limit: r.at_limit,
    })),
    ...modelesIa.map((m) => ({
      label: m.model_name,
      current_usage: m.calls_today,
      limit_value: m.max_calls_per_day,
      percent: m.percent,
      at_warning: m.at_warning,
      at_limit: m.at_limit,
    })),
  ].sort((a, b) => b.percent - a.percent)[0]

  if (!pire) return null

  // v05x-org-neuve (parcours réel du 2026-10-05) : « Nombre de documents : 0 / 50 (0 %) » seul ne disait
  // ni de quoi il s'agissait ni à qui. Le badge dit désormais que c'est le quota DU PROJET OUVERT le plus
  // proche de sa limite, et le lien mène au détail de tous ses quotas.
  return (
    <Link to="/quotas" className="block" data-testid="quota-badge-pied"
          title={t('quota.piedAide', 'Le quota du projet ouvert le plus proche de sa limite (documents, jetons, appels IA…) ; cliquez pour voir tous les quotas du projet.')}>
      <span className="block text-xs text-muted-foreground" data-testid="quota-badge-pied-contexte">
        {t('quota.piedContexte', 'Quota du projet ouvert')}
      </span>
      <QuotaBadge ressource={pire} persistant />
    </Link>
  )
}
